import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, UserPlus, Trash2, KeyRound, Users } from "lucide-react";
import { toast } from "sonner";

const inputCls = "w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-orange-500";
const empty = { name: "", email: "", password: "" };

export const StaffManager = () => {
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => api.get("/my/staff").then((r) => setStaff(r.data)).catch(() => setStaff([]));
  useEffect(() => { load(); }, []);

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); load(); return true; }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (await run(() => api.post("/my/staff", form), `${form.name} can now log in`)) setForm(empty);
  };

  const remove = (s) => run(() => api.delete(`/my/staff/${s.id}`), `${s.name} removed`);

  const reset = async (e) => {
    e.preventDefault();
    if (await run(() => api.post(`/my/staff/${resetFor.id}/reset-password`, { password: newPw }), "Password reset")) { setResetFor(null); setNewPw(""); }
  };

  if (!staff) return <Loader2 className="h-6 w-6 animate-spin text-orange-500" />;

  return (
    <div data-testid="staff-manager" className="space-y-6 max-w-3xl">
      <div>
        <h2 className="font-heading text-3xl font-bold flex items-center gap-2"><Users className="h-7 w-7 text-orange-400" /> Staff</h2>
        <p className="text-slate-400 text-sm mt-1">Staff log in on the same login page. They only see Live Orders, Menu availability and stamp scanning.</p>
      </div>
      <form onSubmit={add} className="bg-slate-900 border border-slate-800 rounded-2xl p-5 grid gap-3 sm:grid-cols-3">
        <input data-testid="staff-name-input" required maxLength={60} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
        <input data-testid="staff-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} />
        <input data-testid="staff-password-input" required minLength={6} placeholder="Password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} />
        <button data-testid="staff-add-button" disabled={busy} className="sm:col-span-3 inline-flex items-center justify-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:opacity-60 rounded-xl py-3 text-sm font-semibold transition-colors">
          <UserPlus className="h-4 w-4" /> Add staff login
        </button>
      </form>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl divide-y divide-slate-800">
        {staff.length === 0 && <p data-testid="staff-empty" className="p-5 text-sm text-slate-400">No staff logins yet.</p>}
        {staff.map((s) => (
          <div key={s.id} data-testid={`staff-row-${s.id}`} className="p-4 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[160px]">
              <p className="font-semibold">{s.name}</p>
              <p className="text-xs text-slate-500">{s.email}</p>
            </div>
            {resetFor?.id === s.id ? (
              <form onSubmit={reset} className="flex gap-2">
                <input data-testid={`staff-new-password-${s.id}`} autoFocus required minLength={6} placeholder="New password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm focus:outline-none" />
                <button data-testid={`staff-reset-confirm-${s.id}`} disabled={busy || newPw.length < 6} className="rounded-lg bg-orange-600 px-3 py-2 text-xs font-semibold disabled:opacity-50">Save</button>
                <button type="button" onClick={() => setResetFor(null)} className="rounded-lg bg-slate-800 px-3 py-2 text-xs">Cancel</button>
              </form>
            ) : (
              <>
                <button data-testid={`staff-reset-${s.id}`} onClick={() => { setResetFor(s); setNewPw(""); }} className="inline-flex items-center gap-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 px-3 py-2 text-xs font-semibold transition-colors"><KeyRound className="h-3.5 w-3.5" /> Reset password</button>
                <button data-testid={`staff-remove-${s.id}`} onClick={() => remove(s)} className="inline-flex items-center gap-1.5 rounded-lg bg-red-950/40 hover:bg-red-950/70 text-red-300 px-3 py-2 text-xs font-semibold transition-colors"><Trash2 className="h-3.5 w-3.5" /> Remove</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
